import React from 'react';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { FormikValues } from 'formik';
import { useNavigate } from 'react-router-dom';
//
import { FormikContext, FormikStep, FormikStepper } from '@components/formik';
import { useAuthContext } from '@components/auth';
import { Api, getToken } from '@utils';
//
import PersonalDetails from './PersonalDetails';
import BankDetails from './BankDetails';
import PublicDetails from './PublicDetails';
import Summary from './Summary';
import BusinessType from './BusinessType';
import CorpBusinessDetails from './CorpBusinessDetails';
import BusinessDetails from './BusinessDetails';
import CoprPersonalDetails from './CoprPersonalDetails';
import CorpSummary from './CorpSummary';
import DocUpload from './DocUpload';

const businessTypeSchema = yup.object({
  country: yup.string().required('Location is required'),
  type: yup.string().required('Type is required'),
});

const personalDetailsSchema = yup.object({
  fName: yup.string().required('First name is required').min(2).max(20).trim(),
  lName: yup.string().required('Last name is required').min(2).max(20).trim(),
  email: yup
    .string()
    .email('Invalid email address')
    .matches(
      /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/,
      'Invalid email address'
    )
    .required('Email is required')
    .trim(),
  birthday: yup
    .date()
    .label('Date of birth')
    .required()
    .max(
      dayjs().subtract(18, 'years').format('YYYY-MM-DD'),
      'You must be at least 18 years old'
    )
    .typeError('Invalid date of birth, must be in DD-MM-YYYY format'),
  home: yup.string().required('Home address is required').trim(),
  address: yup.string().label('Address').required().min(2).max(30).trim(),
  address2: yup.string().trim(),
  city: yup.string().label('City').required().min(2).max(30).trim(),
  province: yup.string().label('Province').required().min(2).max(30).trim(),
  postal: yup
    .string()
    .label('Postal Code')
    .required()
    .min(2)
    .max(30)

    .matches(
      /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
      'Invalid postal code. Ex: A1A1A1 or A1A 1A1 or A1A-1A1'
    )
    .trim(),
});

const publicDetailsSchema = yup.object({
  statementDescriptor: yup
    .string()
    .label('Statement descriptor')
    .required()
    .min(5)
    .max(22)
    .trim(),
  supportPhone: yup.string().label('Support phone number').required().trim(),
  supportUrl: yup
    .string()
    .label('Support URL')
    .required()
    .matches(
      /^(https?:\/\/)+(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*?.?[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/,
      'Invalid (Ex: https://www.example.com or https://example.com)'
    )
    .trim(),
  supportEmail: yup
    .string()
    .label('Support email')
    .email()
    .matches(
      /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/,
      'Invalid email address'
    )
    .required()
    .trim(),
  logo: yup.string().label('Logo'),
  profileLogo: yup.string().label('Profile logo'),
});

const corpBusinessDetailsSchema = yup.object({
  businessName: yup.string().label('Business name').required().trim(),
  businessNumber: yup
    .string()
    .label('Business number')
    .required()
    .matches(/^\d+$/, 'Business number must be a number')
    .max(30)
    .trim(),
  address: yup
    .string()
    .label('Business address')
    .required()
    .min(2)
    .max(30)
    .trim(),
  city: yup.string().label('City').required().min(2).max(30).trim(),
  province: yup.string().label('Province').required().min(2).max(30).trim(),
  postal: yup
    .string()
    .label('Postal Code')
    .required()
    .min(2)
    .max(30)
    .matches(
      /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
      'Invalid postal code. Ex: A1A1A1 or A1A 1A1 or A1A-1A1'
    )
    .trim(),
  industry: yup.string().label('Industry').required(),
  website: yup
    .string()
    .required('Website is required')
    .matches(
      /^(https?:\/\/)+(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*?.?[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/,
      'Invalid (Ex: https://www.example.com or https://example.com)'
    )
    .trim(),
  description: yup.string().required('Description is required').trim(),
});

const businessDetailsSchema = yup.object({
  businessName: yup.string().label('Business name').required().trim(),
  businessNumber: yup
    .string()
    .label('Business number')
    .required()
    .matches(/^\d+$/, 'Business number must be a number')
    .max(30)
    .trim(),
  businessCountry: yup.string().label('Business country').required().trim(),
  website: yup
    .string()
    .required('Website is required')
    .matches(
      /^(https?:\/\/)+(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*?.?[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/,
      'Invalid (Ex: https://www.example.com or https://example.com)'
    )
    .trim(),
  description: yup.string().required('Description is required').trim(),
});

const Onboarding: React.FC = () => {
  const { bankCheckData, steps, setSteps } = React.useContext(FormikContext);
  const { obData, setObData, user } = useAuthContext();

  const [error, setError] = React.useState(false);

  const bankDetailsSchema = yup.object({
    currency: yup.string().label('Currency').required(),
    transit: yup
      .string()
      .label('Transit number')
      .required()
      .test(
        'num',
        'Transit number must be a number',
        (val: any) => val && val.match(/^\d+$/)
      )
      .test(
        'equal',
        'Transit number is not valid',
        (val: any) =>
          bankCheckData.trans_number && bankCheckData.trans_number.includes(val)
      )
      .length(5)
      .trim(),
    institution: yup
      .string()
      .label('Institution number')
      .required()
      .test(
        'num',
        'Transit number must be a number',
        (val: any) => val && val.match(/^\d+$/)
      )
      // .test(
      //   'equal',
      //   'Institution number is not valid',
      //   () => bankCheckData.error !== true
      // )
      .length(3)
      .trim(),
    account: yup
      .string()
      .label('Account number')
      .required()
      .matches(/^\d+$/, 'Account number must be a number')
      .min(7)
      .max(16)
      .trim(),
    confirmAc: yup
      .string()
      .label('Confirm account number')
      .required()
      .oneOf([yup.ref('account'), ''], 'Account numbers must match')
      .trim(),
  });

  const initialValues: FormikValues = {
    country: obData?.country || 'CA',
    type: obData?.type || '',
    fName: obData?.fName || user?.name?.split(' ')[0],
    lName: obData?.lName || user?.name?.split(' ')[1],
    email: obData?.email || user?.email,
    // phone: obData?.phone || '',
    birthday: obData?.birthday ? dayjs(obData.birthday) : '',
    home: obData?.home || 'CA',
    address: obData?.address || '',
    address2: obData?.address2 || '',
    city: obData?.city || '',
    province: obData?.province || 'ontario',
    postal: obData?.postal || '',
    industry: obData?.industry || '',
    website: obData?.website || '',
    description: obData?.description || '',
    businessName: obData?.businessName || '',
    businessNumber: obData?.businessNumber || '',
    businessCountry: obData?.businessCountry || 'CA',
    businessDoing: obData?.businessDoing || '',
    statementDescriptor: obData?.statementDescriptor || '',
    supportPhoneCode: obData?.supportPhoneCode || '',
    supportPhone: obData?.supportPhone || '',
    supportEmail: obData?.supportEmail || '',
    supportUrl: obData?.supportUrl || '',
    currency: obData?.currency || 'cad',
    transit: obData?.transit || '',
    institution: obData?.institution || '',
    account: obData?.account || '',
    confirmAc: obData?.account || '',
    category: obData?.category || '',
    documents: {
      idCard: obData?.documents?.idCard || '',
      driving: obData?.documents?.driving || '',
      utility: obData?.documents?.utility || '',
      vehicle: obData?.documents?.vehicle || '',
    },
    logo: obData?.logo || '',
    profileLogo: obData?.profileLogo || '',
    reqMore: obData?.reqMore || false,
  };

  const navigate = useNavigate();

  // Get the onboarding data from /profile
  React.useEffect(() => {
    const _getObData = async () => {
      const token = getToken();

      try {
        const { data } = await Api.get('/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Retry');

        if (data.data.onboarding?.id !== '000000000000000000000000') {
          setObData(data.data.onboarding);
        }
      } catch (e) {
        console.log(e);
      }
    };

    !obData || (obData?.id === '000000000000000000000000' && _getObData());
  }, [steps]);

  // Handle the form submission
  const onSubmit = async (values: FormikValues) => {
    const token = getToken();

    // Add completed:true in values
    values.completed = true;

    try {
      const { data } = await Api.put('/onboarding', values, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // console.log(data);

      if (data.data.token && data.data.token !== '') {
        localStorage.setItem('sidToken', data.data.token);

        navigate('/onboarding/completed', {
          state: { token: data.data.token },
        });
      }
      setError(false);
    } catch (e) {
      console.log(e);
      // values.completed = false;
      setError(true);
      navigate('/onboarding/completed');
    }
  };

  return (
    <FormikStepper
      initialValues={initialValues}
      onSubmit={onSubmit}
      error={error}
      setError={setError}
    >
      <FormikStep label="Verify your business">
        <FormikStep validationSchema={businessTypeSchema} label="Business type">
          <BusinessType />
        </FormikStep>
        <FormikStep
          validationSchema={personalDetailsSchema}
          label="Personal details"
          type="sole"
        >
          <PersonalDetails />
        </FormikStep>
        <FormikStep
          label="Business details"
          validationSchema={corpBusinessDetailsSchema}
          notFor="sole"
        >
          <CorpBusinessDetails />
        </FormikStep>
        <FormikStep
          validationSchema={personalDetailsSchema}
          label="Business representative"
          notFor="sole"
        >
          <CoprPersonalDetails />
        </FormikStep>
        <FormikStep
          label="Business details"
          validationSchema={businessDetailsSchema}
          type="sole"
        >
          <BusinessDetails />
        </FormikStep>
        <FormikStep
          label="Public details"
          validationSchema={publicDetailsSchema}
        >
          <PublicDetails />
        </FormikStep>
      </FormikStep>

      <FormikStep label="Add your bank">
        <FormikStep
          label="Business details"
          validationSchema={bankDetailsSchema}
        >
          <BankDetails />
        </FormikStep>
      </FormikStep>

      <FormikStep label="Verify your documents">
        <FormikStep label="Document upload">
          <DocUpload />
        </FormikStep>
      </FormikStep>

      {/* <FormikStep label="Secure your account">
        <FormikStep label="Two-step authentication">
          <TwoStepAuth />
        </FormikStep>
      </FormikStep> */}

      {/* <FormikStep label="Add extras">
        <FormikStep label="Tax calculation">
          <TaxCalculation />
        </FormikStep>
      </FormikStep> */}

      <FormikStep label="Agree and Submit">
        <FormikStep label="Summary" notFor="sole">
          <CorpSummary />
        </FormikStep>
        <FormikStep label="Summary" type="sole">
          <Summary />
        </FormikStep>
      </FormikStep>
    </FormikStepper>
  );
};

export default Onboarding;
